import { useState, useRef, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  CardDescription, 
  CardTitle, 
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding } from "@/hooks/use-branding";
import { toast } from "@/lib/toast";
import { Upload, Loader2, Trash2, AlertCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getContrastColor } from "@/lib/utils";

export default function BrandingTab() {
  const { user } = useAuth();
  const { branding, logoUrl, primaryColor: initialColor, isLoading, updateBranding, fetchBranding } = useBranding();
  const [primaryColor, setPrimaryColor] = useState(initialColor || '#0448ff');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Update color input when branding data changes
  useEffect(() => {
    if (initialColor) {
      setPrimaryColor(initialColor);
    }
  }, [initialColor]);
  
  // Function to check if PNG has transparency
  const checkPngTransparency = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const img = new Image();
        
        img.onload = () => {
          // Create canvas to check image data
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            resolve(false);
            return;
          }
          
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas
          ctx.drawImage(img, 0, 0);
          
          // Check for transparency in the image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          // Check if alpha channel exists with values < 255
          for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) {
              resolve(true); // Found transparency
              return;
            }
          }
          
          resolve(false); // No transparency found
        };
        
        img.src = event.target?.result as string;
      };
      
      reader.readAsDataURL(file);
    });
  };
  
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setError(null);
      
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }
      
      const file = e.target.files[0];
      
      // Validate file is PNG
      if (file.type !== 'image/png') {
        setError("Apenas imagens PNG são permitidas");
        return;
      }
      
      setIsUploading(true);
      
      // Validate PNG has transparency
      const hasTransparency = await checkPngTransparency(file);
      
      if (!hasTransparency) {
        setError("Apenas imagens PNG sem fundo (com transparência) são permitidas");
        setIsUploading(false);
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError("O tamanho máximo permitido é 2MB");
        setIsUploading(false);
        return;
      }
      
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error("Usuário não autenticado");
        setIsUploading(false);
        return;
      }
      
      // Generate a unique filename with timestamp to prevent caching issues
      const timestamp = Date.now();
      const fileName = `logo-${session.user.id}-${timestamp}.png`;
      
      console.log("Uploading logo:", fileName);
      
      try {
        // Upload to storage bucket
        const { error: uploadError, data } = await supabase.storage
          .from('avatars')
          .upload(fileName, file, {
            upsert: true,
            cacheControl: 'no-cache, no-store',
            contentType: 'image/png'
          });
          
        if (uploadError) {
          console.error("Upload error:", uploadError);
          setError(uploadError.message || "Erro ao fazer upload do logo");
          setIsUploading(false);
          return;
        }
        
        console.log("Upload successful, data:", data);
        
        // Get public URL with cache-busting parameter
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);
          
        // Add cache-busting parameter
        const logoUrl = `${publicUrl}?t=${timestamp}`;
        
        console.log("Public URL generated:", logoUrl);
        
        // Update branding with new logo URL
        const updateResult = await updateBranding({ logo_url: logoUrl });
        
        if (!updateResult) {
          throw new Error("Falha ao atualizar informações de marca");
        }
        
        console.log("Branding updated with logo URL:", logoUrl);
        
        // Force refresh branding data
        await fetchBranding();
        
        toast.success("Logo atualizado com sucesso!");
      } catch (error: any) {
        console.error("Error in upload process:", error);
        setError("Erro no processo de upload: " + (error.message || "Erro desconhecido"));
      }
    } catch (error: any) {
      console.error("Erro ao fazer upload do logo:", error);
      setError(error.message || "Erro ao fazer upload do logo");
    } finally {
      setIsUploading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await updateBranding({ logo_url: null });
      await fetchBranding();
      toast.success("Logo removido com sucesso!");
    } catch (error) {
      console.error("Erro ao remover logo:", error);
      toast.error("Erro ao remover logo");
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const result = await updateBranding({ primary_color: primaryColor });
      
      if (!result) {
        throw new Error("Failed to update brand color");
      }
      
      await fetchBranding();
      toast.success("Cor personalizada salva com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar cor:", error);
      toast.error("Erro ao salvar cor personalizada");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personalização de Marca</CardTitle>
        <CardDescription>
          Personalize sua marca com logo e cores personalizadas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <Label>Logo da Empresa</Label>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="p-6 border rounded-md flex items-center justify-center w-32 h-32">
                  <BrandLogo agencyName={user?.agency_name} size="lg" />
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/png"
                    onChange={handleLogoUpload}
                    disabled={isUploading}
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Enviar Logo
                      </>
                    )}
                  </Button>
                  {logoUrl && (
                    <Button
                      variant="destructive"
                      onClick={handleRemoveLogo}
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remover Logo
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Apenas PNG sem fundo permitido. Tamanho máximo: 2MB.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="primaryColor">Cor Principal</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="color"
                  id="primaryColor"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-24 h-10 p-1 cursor-pointer"
                />
                <Input 
                  type="text" 
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Esta cor será usada como tema principal em toda a aplicação.
              </p>
            </div>

            <div className="pt-4">
              <h3 className="font-medium mb-2">Previsualização</h3>
              <div className="flex flex-wrap gap-4">
                <div 
                  className="p-4 rounded-md text-center flex items-center justify-center"
                  style={{ 
                    backgroundColor: primaryColor,
                    color: getContrastColor(primaryColor),
                    width: '100px',
                    height: '60px'
                  }}
                >
                  Botão
                </div>
                <div 
                  className="p-4 border rounded-md text-center flex items-center justify-center"
                  style={{ 
                    borderColor: primaryColor,
                    color: primaryColor,
                    width: '100px',
                    height: '60px'
                  }}
                >
                  Contorno
                </div>
                <div 
                  className="p-4 rounded-md text-center flex items-center justify-center"
                  style={{ 
                    backgroundColor: `${primaryColor}20`,
                    color: primaryColor,
                    width: '100px',
                    height: '60px'
                  }}
                >
                  Destaque
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleSave} 
          disabled={isSaving || isLoading}
          className="ml-auto"
          style={{ 
            backgroundColor: primaryColor,
            color: getContrastColor(primaryColor) 
          }}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : "Salvar Alterações"}
        </Button>
      </CardFooter>
    </Card>
  );
}
